import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, KeyRound, Loader2, UserCheck, User } from "lucide-react";

export default function AdminSuperAdmin() {
  const navigate = useNavigate();
  const isSuperAdmin = localStorage.getItem("admin_is_super") === "1";

  const [admins, setAdmins]         = useState([]);
  const [loading, setLoading]       = useState(true);

  // Add admin form
  const [addForm, setAddForm]       = useState({ email: "", password: "", confirm: "" });
  const [adding, setAdding]         = useState(false);

  // Password change modal
  const [pwModal, setPwModal]       = useState(null); // { id, email }
  const [pwForm, setPwForm]         = useState({ password: "", confirm: "" });
  const [pwSaving, setPwSaving]     = useState(false);

  // Delete confirmation
  const [deleting, setDeleting]     = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.listAdmins();
      setAdmins(data.admins || []);
    } catch {
      toast.error("Failed to load admins");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) { navigate("/admin/dashboard"); return; }
    load();
  }, [isSuperAdmin, navigate, load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (addForm.password !== addForm.confirm) { toast.error("Passwords do not match"); return; }
    if (addForm.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setAdding(true);
    try {
      const { data } = await api.createAdmin(addForm.email, addForm.password);
      setAdmins((prev) => [...prev, data.admin]);
      setAddForm({ email: "", password: "", confirm: "" });
      toast.success(`Admin account created for ${addForm.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create admin");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete admin account for ${a.email}? This cannot be undone.`)) return;
    setDeleting(a.id);
    try {
      await api.deleteAdmin(a.id);
      setAdmins((prev) => prev.filter((x) => x.id !== a.id));
      toast.success(`${a.email} removed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete admin");
    } finally {
      setDeleting(null);
    }
  };

  const openPwModal = (a) => {
    setPwModal({ id: a.id, email: a.email });
    setPwForm({ password: "", confirm: "" });
  };

  const handlePwSave = async () => {
    if (pwForm.password !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
    if (pwForm.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setPwSaving(true);
    try {
      await api.changeAdminPassword(pwModal.id, pwForm.password);
      toast.success(`Password updated for ${pwModal.email}`);
      setPwModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setPwSaving(false);
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <ShieldCheck className="w-7 h-7 text-amber-600" /> Super Admin
        </h1>
        <p className="text-slate-400 text-sm mt-1">Manage administrator accounts. Only the Super Admin can access this page.</p>
      </div>

      {/* ── Admin Accounts List ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <h2 className="font-semibold text-slate-700 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Administrator Accounts ({admins.length})
          </h2>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-green-700" />
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {admins.map((a) => (
              <div key={a.id} className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50/40 transition-colors">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${a.is_super_admin ? "bg-amber-100" : "bg-slate-100"}`}>
                  {a.is_super_admin
                    ? <ShieldCheck className="w-4.5 h-4.5 text-amber-600" />
                    : <User className="w-4.5 h-4.5 text-slate-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate" data-testid={`admin-email-${a.id}`}>{a.email}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${a.is_super_admin ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                      {a.is_super_admin ? <><ShieldCheck className="w-3 h-3" /> Super Admin</> : <><UserCheck className="w-3 h-3" /> Admin</>}
                    </span>
                    {a.created_at && (
                      <span className="text-xs text-slate-400">Added {new Date(a.created_at).toLocaleDateString("en-IE")}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openPwModal(a)}
                    data-testid={`change-password-${a.id}`}
                    className="border-slate-200 text-slate-600 hover:bg-slate-50 gap-1.5 text-xs">
                    <KeyRound className="w-3.5 h-3.5" /> Change Password
                  </Button>
                  {!a.is_super_admin && (
                    <Button size="sm" variant="outline" onClick={() => handleDelete(a)}
                      disabled={deleting === a.id}
                      data-testid={`delete-admin-${a.id}`}
                      className="border-red-200 text-red-600 hover:bg-red-50">
                      {deleting === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Add New Admin ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <h2 className="font-semibold text-slate-700 text-sm flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <Plus className="w-4 h-4 text-green-700" /> Add New Admin
          </h2>
        </div>
        <form onSubmit={handleAdd} className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-slate-600 font-medium mb-1.5 block text-xs uppercase tracking-wide">Email Address</Label>
            <Input type="email" required placeholder="admin@example.com" data-testid="new-admin-email-input"
              value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
              className="h-11 bg-slate-50 border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-600 font-medium mb-1.5 block text-xs uppercase tracking-wide">Password</Label>
            <Input type="password" required placeholder="Min. 8 characters" data-testid="new-admin-password-input"
              value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
              className="h-11 bg-slate-50 border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-600 font-medium mb-1.5 block text-xs uppercase tracking-wide">Confirm Password</Label>
            <Input type="password" required placeholder="Repeat password" data-testid="new-admin-confirm-input"
              value={addForm.confirm} onChange={(e) => setAddForm({ ...addForm, confirm: e.target.value })}
              className="h-11 bg-slate-50 border-slate-200" />
          </div>
          <div className="sm:col-span-3 flex justify-end pt-1">
            <Button type="submit" disabled={adding} data-testid="add-admin-submit-btn"
              className="bg-[#15803d] hover:bg-green-800 text-white gap-2 px-6">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {adding ? "Creating..." : "Create Admin Account"}
            </Button>
          </div>
        </form>
      </div>

      {/* ── Change Password Modal ── */}
      <Dialog open={!!pwModal} onOpenChange={(o) => !o && setPwModal(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }} className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-slate-600" /> Change Password
            </DialogTitle>
            {pwModal && <p className="text-xs text-slate-400 mt-1">{pwModal.email}</p>}
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-slate-600 font-medium mb-1.5 block">New Password</Label>
              <Input type="password" placeholder="Min. 8 characters" data-testid="new-password-input"
                value={pwForm.password} onChange={(e) => setPwForm({ ...pwForm, password: e.target.value })}
                className="h-11 bg-slate-50 border-slate-200" autoFocus />
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-1.5 block">Confirm New Password</Label>
              <Input type="password" placeholder="Repeat new password" data-testid="confirm-password-input"
                value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handlePwSave()}
                className="h-11 bg-slate-50 border-slate-200" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwModal(null)} disabled={pwSaving}>Cancel</Button>
            <Button onClick={handlePwSave} disabled={pwSaving} data-testid="save-password-btn"
              className="bg-[#15803d] hover:bg-green-800 text-white gap-2">
              {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {pwSaving ? "Saving..." : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
